// Model inbox notifikasi: meta tampilan, waktu relatif, filter <-> query. Murni.
import { fromApiDatetime, DISPLAY_DATE } from './apiDatetime.js';

export const PAGE_SIZE = 20;

export const SEVERITY_META = {
  CRITICAL: { key: 'critical', label: 'Critical', color: '#dc3545' },
  WARNING: { key: 'warning', label: 'Warning', color: '#fd7e14' },
  INFO: { key: 'info', label: 'Info', color: '#0d6efd' },
};

export const CATEGORY_META = {
  ALERT: { label: 'Alert', icon: 'ri-alarm-warning-line' },
  EVENT: { label: 'Event', icon: 'ri-flashlight-line' },
  REPORT: { label: 'Report', icon: 'ri-file-chart-line' },
  ASSET: { label: 'Asset', icon: 'ri-ship-2-line' },
  SYSTEM: { label: 'System', icon: 'ri-server-line' },
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const ID_QUERY_KEYS = ['category', 'severity', 'compId', 'massId'];

const hasKey = (meta, key) => Object.prototype.hasOwnProperty.call(meta, key);

// id & halaman valid cuma bilangan bulat positif
function positiveInt(value) {
  const text = String(value ?? '');
  return /^[1-9]\d{0,9}$/.test(text) ? Number(text) : null;
}

export function severityMeta(severity) {
  return hasKey(SEVERITY_META, severity) ? SEVERITY_META[severity] : SEVERITY_META.INFO;
}

export function categoryMeta(category) {
  return hasKey(CATEGORY_META, category) ? CATEGORY_META[category] : CATEGORY_META.SYSTEM;
}

// START dan SINGLE gak dapat badge.
export function phaseBadge(phase) {
  if (phase === 'REMINDER') return { label: 'Reminder', variant: 'secondary' };
  if (phase === 'RESOLVE') return { label: 'Resolved', variant: 'success' };
  return null;
}

export function relativeTime(iso, nowMs) {
  const atMs = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(atMs)) return '';
  const diffMs = nowMs - atMs;
  if (diffMs < MINUTE_MS) return 'just now';
  if (diffMs < HOUR_MS) return `${Math.floor(diffMs / MINUTE_MS)} min ago`;
  if (diffMs < DAY_MS) return `${Math.floor(diffMs / HOUR_MS)} h ago`;
  if (diffMs < 7 * DAY_MS) return `${Math.floor(diffMs / DAY_MS)} d ago`;
  return fromApiDatetime(iso).format(DISPLAY_DATE);
}

export function isUnread(item) {
  return Boolean(item) && !item.readAt;
}

// Jangkar read-all: id terbesar yang lagi tampil.
export function maxId(items) {
  return (items || []).reduce((max, item) => {
    const id = positiveInt(item?.id);
    return id !== null && (max === null || id > max) ? id : max;
  }, null);
}

// tandai dibaca per id atau sampai upToId (inklusif)
export function markReadUpTo(items, { id, upToId, readAt }) {
  return (items || []).map((item) => {
    const hit = id ? item.id === id : item.id <= upToId;
    return hit && !item.readAt ? { ...item, readAt } : item;
  });
}

export function needsCompanySwitch(item, activeCompId) {
  const compId = positiveInt(item?.compId);
  return compId !== null && compId !== positiveInt(activeCompId);
}

export function emptyFilters() {
  return {
    unreadOnly: false,
    category: '',
    severity: '',
    compId: null,
    massId: null,
    page: 1,
  };
}

// vessel dikunci ke company aktif
export function applyFilters(filters, patch, activeCompId) {
  const active = positiveInt(activeCompId);
  const next = { ...filters, page: 1, ...patch };
  if ('compId' in patch && next.compId !== active) next.massId = null;
  if (!active) next.massId = null;
  if (next.massId) next.compId = active;
  return next;
}

// compId tanpa kontrol di layar cuma boleh ada selama vessel terpilih
export function withoutHiddenCompany(filters, companyVisible) {
  if (companyVisible || filters.massId || filters.compId === null) return filters;
  return { ...filters, compId: null };
}

export function toInboxParams(filters) {
  const params = { page: positiveInt(filters.page) || 1, max: PAGE_SIZE };
  if (filters.unreadOnly === true) params.unread = 1;
  if (hasKey(CATEGORY_META, filters.category)) params.category = filters.category;
  if (hasKey(SEVERITY_META, filters.severity)) params.severity = filters.severity;
  const compId = positiveInt(filters.compId);
  if (compId) params.compId = compId;
  const massId = positiveInt(filters.massId);
  if (massId) params.massId = massId;
  return params;
}

// '' kalau semua filter default.
export function filtersToQuery(filters) {
  const params = toInboxParams(filters);
  const query = new URLSearchParams();
  if (params.unread) query.set('unread', '1');
  ID_QUERY_KEYS.forEach((key) => {
    if (params[key]) query.set(key, String(params[key]));
  });
  if (params.page > 1) query.set('page', String(params.page));
  const text = query.toString();
  return text ? `?${text}` : '';
}

// Nilai cacat dibuang diam-diam.
export function queryToFilters(search, activeCompId) {
  const query = new URLSearchParams(search);
  const patch = {
    unreadOnly: query.get('unread') === '1',
    massId: positiveInt(query.get('massId')),
    page: positiveInt(query.get('page')) || 1,
  };
  const category = query.get('category');
  if (hasKey(CATEGORY_META, category)) patch.category = category;
  const severity = query.get('severity');
  if (hasKey(SEVERITY_META, severity)) patch.severity = severity;
  const compId = positiveInt(query.get('compId'));
  if (compId) patch.compId = compId;
  return applyFilters(emptyFilters(), patch, activeCompId);
}
